export type Language = 'fr' | 'en'

export interface Translation {
  nav: {
    features: string
    results: string
    cases: string
    home: string
    main: string
    mobile: string
    openMenu: string
    closeMenu: string
    changeLanguage: string
  }
  cta: {
    login: string
    demo: string
  }
  hero: {
    highlight: string
    titleRest: string
    subtitleBefore: string
    subtitleStrong: string
    subtitleAfter: string
    demoDuration: string
  }
  socialProof: {
    usedBy: string
    managers: string
    on: string
  }
  logos: {
    label: string
  }
  showcase: {
    sectionLabel: string
    play: string
    previewAlt: string
    dialogLabel: string
    iframeTitle: string
    loading: string
    close: string
  }
  why: {
    eyebrow: string
    titleBefore: string
    highlight: string
    titleAfter: string
    statLabel: string
    statSource: string
  }
  solution: {
    eyebrow: string
    titleBefore: string
    highlight: string
    titleAfter: string
    intro: string[]
    simpleTitleBefore: string
    simpleTitleHighlight: string
    simpleTitleAfter: string
    simpleText: string
    simpleAlt: string
  }
  results: {
    eyebrow: string
    titleBefore: string
    highlight: string
    titleAfter: string
    intro: string
    engagementLabel: string
    timeLabel: [string, string]
    turnoverLabel: string
    source: string
    engagementSource: string
    engagementAlt: string
    portraitAlt: string
    duoAlt: string
    highlights: string[]
  }
  cases: {
    eyebrow: string
    titleBefore: string
    highlight: string
    carouselLabel: string
    objective: string
    context: string
    storeAlt: string
    prev: string
    next: string
  }
  finalCta: {
    titleBefore: string
    highlight: string
    titleAfter: string
    subtitle: string
    note: string
  }
  footer: {
    text: string
  }
}

export const translations: Record<Language, Translation> = {
  fr: {
    nav: {
      features: 'Fonctionnalités',
      results: 'Résultats',
      cases: 'Cas client',
      home: 'Accueil Rewayz',
      main: 'Navigation principale',
      mobile: 'Navigation mobile',
      openMenu: 'Ouvrir le menu',
      closeMenu: 'Fermer le menu',
      changeLanguage: 'Changer de langue'
    },
    cta: {
      login: 'Se connecter',
      demo: 'Réserver une démo'
    },
    hero: {
      highlight: 'Le copilote IA',
      titleRest: 'qui aide vos managers à prendre les bonnes décisions',
      subtitleBefore: 'Détectez les signaux faibles de vos équipes,',
      subtitleStrong: 'guidez vos managers vers les bonnes actions',
      subtitleAfter: 'pour les aider à mieux performer au quotidien',
      demoDuration: 'Démo de 30 minutes'
    },
    socialProof: {
      usedBy: 'Utilisé par',
      managers: '+5000 managers',
      on: 'sur'
    },
    logos: {
      label: 'Ils nous font confiance'
    },
    showcase: {
      sectionLabel: 'Vidéo de démonstration Rewayz',
      play: 'Lire la vidéo de démonstration',
      previewAlt: "Aperçu de l'interface Rewayz : recommandation d'action pour un manager",
      dialogLabel: 'Démonstration Rewayz',
      iframeTitle: 'Démonstration interactive Rewayz',
      loading: 'Chargement de la démonstration…',
      close: 'Fermer'
    },
    why: {
      eyebrow: 'Pourquoi Rewayz ?',
      titleBefore: 'Pour transformer les situations managériales',
      highlight: 'en actions concrètes',
      titleAfter: '',
      statLabel: "de l'engagement de vos équipes dépend de vos managers",
      statSource: 'Source : Gallup – Employee Engagement Strategies for 2026'
    },
    solution: {
      eyebrow: 'Pour vos managers',
      titleBefore: 'Simplifie le quotidien de vos managers, les aide à mieux agir et',
      highlight: 'leur donne les moyens de réussir',
      titleAfter: '',
      intro: [
        'Rewayz **comprend les situations de terrain**, **identifie les actions les plus pertinentes** et accompagne les managers dans leur mise en œuvre.',
        "Chaque résultat **enrichit l'expérience collective** dans le contexte de votre entreprise."
      ],
      simpleTitleBefore: 'Une',
      simpleTitleHighlight: 'intelligence qui grandit',
      simpleTitleAfter: 'avec votre entreprise.',
      simpleText: "Chaque action menée et chaque résultat observé enrichissent l'expérience collective, pour des recommandations toujours plus pertinentes et adaptées à la réalité de votre entreprise.",
      simpleAlt: "Deux personnes consultant l'application Rewayz sur un ordinateur portable"
    },
    results: {
      eyebrow: 'Résultats',
      titleBefore: 'Des résultats',
      highlight: 'mesurés',
      titleAfter: 'sur le terrain',
      intro: "L'engagement de vos équipes est un levier direct de performance",
      engagementLabel: "d'engagement en 4 mois",
      timeLabel: ['gagnées par semaine', "pour permettre aux managers de se concentrer sur l'essentiel"],
      turnoverLabel: 'de turnover en un an',
      source: "Résultats obtenus auprès d'un échantillon de 5 000 managers au S1 2026",
      engagementSource:
        "Résultats obtenus auprès d'un échantillon de 5 000 managers au S1 2026. Évolution de l'engagement sur 4 mois",
      engagementAlt: "Manageuse prenant des notes lors d'un point d'équipe",
      portraitAlt: 'Manageuse souriante, mains sur les hanches, dans un bureau lumineux',
      duoAlt: 'Deux collaborateurs travaillant ensemble sur un ordinateur portable',
      highlights: ['Managers plus efficaces', '25 000€ à 35 000€ économisés par an', 'Moins de turnover']
    },
    cases: {
      eyebrow: "Cas d'usage",
      titleBefore: 'Nos cas',
      highlight: 'clients',
      carouselLabel: 'Cas clients',
      objective: 'Objectif',
      context: 'Contexte',
      storeAlt: 'Magasin',
      prev: 'Cas client précédent',
      next: 'Cas client suivant'
    },
    finalCta: {
      titleBefore: 'Prêt à',
      highlight: 'équiper',
      titleAfter: 'vos managers ?',
      subtitle: "Offrez à vos managers le copilote pour prendre les bonnes décisions",
      note: "Démo de 30 minutes"
    },
    footer: {
      text: '© Rewayz 2026 - Tous droits réservés — ( 🚧 site en construction)'
    }
  },
  en: {
    nav: {
      features: 'Features',
      results: 'Results',
      cases: 'Customer stories',
      home: 'Rewayz home',
      main: 'Main navigation',
      mobile: 'Mobile navigation',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      changeLanguage: 'Change language'
    },
    cta: {
      login: 'Log in',
      demo: 'Book a demo'
    },
    hero: {
      highlight: 'The AI copilot',
      titleRest: 'that helps your managers make the right decisions',
      subtitleBefore: 'Spot the weak signals in your teams,',
      subtitleStrong: 'guide your managers toward the right actions',
      subtitleAfter: 'and help them perform better every day',
      demoDuration: '30-minute demo'
    },
    socialProof: {
      usedBy: 'Used by',
      managers: '5,000+ managers',
      on: 'on'
    },
    logos: {
      label: 'They trust us'
    },
    showcase: {
      sectionLabel: 'Rewayz demo video',
      play: 'Play the demo video',
      previewAlt: 'Preview of the Rewayz interface: an action recommendation for a manager',
      dialogLabel: 'Rewayz demo',
      iframeTitle: 'Rewayz interactive demo',
      loading: 'Loading the demo…',
      close: 'Close'
    },
    why: {
      eyebrow: 'Why Rewayz?',
      titleBefore: 'To turn managerial situations',
      highlight: 'into concrete actions',
      titleAfter: '',
      statLabel: "of your teams' engagement depends on their managers",
      statSource: 'Source: Gallup – Employee Engagement Strategies for 2026'
    },
    solution: {
      eyebrow: 'For your managers',
      titleBefore: "Simplifies your managers' day-to-day, helps them act better and",
      highlight: 'gives them the means to succeed',
      titleAfter: '',
      intro: [
        'Rewayz **understands situations on the ground**, **identifies the most relevant actions** and supports managers as they put them into practice.',
        "Every result **enriches your company's collective experience**, in its own context."
      ],
      simpleTitleBefore: 'An',
      simpleTitleHighlight: 'intelligence that grows',
      simpleTitleAfter: 'with your company.',
      simpleText: "Every action taken and every result observed enriches your collective experience, for recommendations that are ever more relevant and suited to your company's reality.",
      simpleAlt: 'Two people looking at the Rewayz app on a laptop'
    },
    results: {
      eyebrow: 'Results',
      titleBefore: '',
      highlight: 'Measured',
      titleAfter: 'results in the field',
      intro: "Your teams' engagement is a direct driver of performance",
      engagementLabel: 'engagement in 4 months',
      timeLabel: ['saved every week', 'so managers can focus on what matters most'],
      turnoverLabel: 'turnover in one year',
      source: 'Results from a sample of 5,000 managers in H1 2026',
      engagementSource:
        'Results from a sample of 5,000 managers in H1 2026. Change in engagement over 4 months',
      engagementAlt: 'Manager taking notes during a team meeting',
      portraitAlt: 'Smiling manager, hands on hips, in a bright office',
      duoAlt: 'Two colleagues working together on a laptop',
      highlights: ['More effective managers', '€25,000 to €35,000 saved per year', 'Lower turnover']
    },
    cases: {
      eyebrow: 'Use cases',
      titleBefore: 'Our customer',
      highlight: 'stories',
      carouselLabel: 'Customer stories',
      objective: 'Goal',
      context: 'Context',
      storeAlt: 'Store',
      prev: 'Previous customer story',
      next: 'Next customer story'
    },
    finalCta: {
      titleBefore: 'Ready to',
      highlight: 'equip',
      titleAfter: 'your managers?',
      subtitle: 'Give them the copilot that turns uncertainty into action.',
      note: '30-min demo, then a 2-month trial'
    },
    footer: {
      text: '© Rewayz 2026 - All rights reserved — ( 🚧 site under construction)'
    }
  }
}
